import { useEffect, useRef } from 'react';
import { useIsAuthenticated, useUserState } from '@features/user';
import { UTMTracker } from '@services/utm';
import { useStatsigUser } from '@statsig/react-bindings';

export const useStatsigUserSync = () => {
  const isAuthenticated = useIsAuthenticated();
  const userState = useUserState({
    skip: !isAuthenticated,
  });
  const { updateUserAsync } = useStatsigUser();
  const previousUserId = useRef<string | undefined>();
  const previousUTMs = useRef<string>('');

  useEffect(() => {
    // Get current UTM parameters
    const currentUTMs = UTMTracker.getUTMs();
    const utmString = JSON.stringify(currentUTMs);

    if (!userState) {
      // User logged out - reset Statsig user but keep UTMs for attribution
      if (previousUserId.current) {
        updateUserAsync({
          custom: {
            // Keep UTM parameters even for logged out users
            ...currentUTMs,
          },
        }).catch((error: unknown) => {
          console.error('Failed to reset Statsig user:', error);
        });
        previousUserId.current = undefined;
      }
      return;
    }

    const currentUserId = userState.user_id;

    // Update if user changed or UTMs changed
    const shouldUpdate =
      (currentUserId && currentUserId !== previousUserId.current) ||
      (utmString !== previousUTMs.current && Object.keys(currentUTMs).length > 0);

    if (shouldUpdate) {
      updateUserAsync({
        userID: currentUserId,
        email: userState.userInfo?.email,
        custom: {
          // User properties
          institution: userState.userInfo?.institution,
          specialization: userState.userInfo?.specialization,
          customerType: userState.userInfo?.customerType,
          featureSet: userState.userInfo?.featureSet,
          roles: userState.userInfo?.roles,

          // Account status
          paymentStatus: userState.paymentStatus,
          emailVerified: userState.emailVerified,

          // Group information
          groupIds: userState.group_ids,
          groupUserRoles: userState.group_user_roles,

          // Additional metadata
          firstLoginTime: userState.firstLoginTime,
          patientMatchEnrolled: userState.patientMatchEnrolled,
          clearinghouseLinked: userState.clearinghouseLinked,

          // New user identification for experiments
          is_new_user: !userState.onboardingSteps?.account_creation_onboarding_completed,
          signup_date: userState.firstLoginTime, // Unix timestamp in seconds from API
          days_since_signup: userState.firstLoginTime 
            ? Math.floor((Date.now()/1000 - userState.firstLoginTime) / (60 * 60 * 24))
            : undefined,

          // UTM Parameters for attribution
          ...currentUTMs,

          // Feature flags
          ...userState.feature_flags,
        },
      }).catch((error: unknown) => {
        console.error('Failed to update Statsig user:', error);
      });

      previousUserId.current = currentUserId;
      previousUTMs.current = utmString;
    }
  }, [userState, updateUserAsync]);
};
